import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Box, Typography, TextField, Autocomplete, IconButton,
    InputAdornment, CircularProgress, Grid, Divider,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, FormControlLabel, Checkbox
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete'; // <--- THÊM MỚI
import AddIcon from '@mui/icons-material/Add';       // <--- THÊM MỚI
import VisibilityIcon from '@mui/icons-material/Visibility'; 
import { formatCurrency } from '../utils/currencyUtils';
import * as API from '../api';
import BillDetailDialog from './BillDetailDialog';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';

const CustomLabel = ({ title, required = false }) => (
    <Typography variant="caption" sx={{ fontWeight: 'bold', mb: 0.5, color: '#555', textTransform: 'uppercase', display: 'block' }}>
        {title} {required && <span style={{ color: 'red' }}>*</span>}
    </Typography>
);

// Danh sách tĩnh cho Dropdown Đối tác
const PARTNER_OPTIONS = [
    { id: 'CUSTOMER', label: 'Khách hàng' },
    { id: 'SUPPLIER', label: 'Nhà cung cấp' }
];

const getAutoText = (item, partnerId) => {
    if (!item) return '';
    
    if (partnerId === 'CUSTOMER') {
        if (item.sender_info) return item.sender_info;
        
        const cName = item.customer_name || item.name || 'Khách hàng';
        const cSource = item.source ? ` (${item.source})` : '';
        return `${cName}${cSource}`;
    } 
    else if (partnerId === 'SUPPLIER') {
        const sName = item.supplier_name || (item.supplier && item.supplier.name) || '';
        const sInfo = item.bank_info || item.receiver_info;
        
        if (sName && sInfo) return `${sName} - ${sInfo}`;
        if (sName) return sName;
        return sInfo || '';
    }
    return '';
};

const BulkPaymentDialog = ({ 
    open, 
    onClose, 
    items = [], 
    type = 'PAYABLE',
    distributionSummary,
    onSuccess, 
    showToast,
    readOnly = false,
    savedTransactions = [],
    snapshotData = null
}) => {
    // --- State cơ bản ---
    const [amountStr, setAmountStr] = useState('');
    const [realAmount, setRealAmount] = useState(0);
    const [note, setNote] = useState('');
    const [billFile, setBillFile] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [shareholders, setShareholders] = useState([]); 
    const [calculatedTransactions, setCalculatedTransactions] = useState([]);
    const [manualTransactions, setManualTransactions] = useState([]); 
    const [combinedAccounts, setCombinedAccounts] = useState([]);
    const [transferSuggestions, setTransferSuggestions] = useState([]);
    const [viewBillDetail, setViewBillDetail] = useState(null);

    // --- State Mode (Thu/Chi) cho các loại thanh toán thường ---
    const [billMode, setBillMode] = useState('OUT'); // 'IN' (Thu) | 'OUT' (Chi)

    // --- Data Source ---
    const [companyAccounts, setCompanyAccounts] = useState([]);

    // --- Selection State ---
    const [selectedCompanyAcc, setSelectedCompanyAcc] = useState(null);
    const [selectedPartnerType, setSelectedPartnerType] = useState(null);

    // Text Display/Input
    const [senderText, setSenderText] = useState('');
    const [receiverText, setReceiverText] = useState('');
    const [transactionDate, setTransactionDate] = useState(new Date().toISOString().slice(0, 16));

    const isUnifiedMode = type === 'EXPENSE_DISTRIBUTION' || type === 'CASHFLOW_DISTRIBUTION';
    const [isSimpleMode, setIsSimpleMode] = useState(false);

    const getTitle = () => {
        if (type === 'EXPENSE_DISTRIBUTION') return 'PHÂN BỔ CHI PHÍ CHUNG (GỘP)';
        if (type === 'CASHFLOW_DISTRIBUTION') return 'PHÂN BỔ DÒNG TIỀN THỰC TẾ'; // <--- THÊM DÒNG NÀY
        if (isUnifiedMode) return 'PHÂN BỔ LỢI NHUẬN & CÔNG NỢ (GỘP)';
        return billMode === 'IN' ? 'XÁC NHẬN THU TIỀN (IN)' : 'XÁC NHẬN CHI TIỀN (OUT)';
    };

    // --- Load Data & Init ---
    useEffect(() => {
        if (open) {
            // Reset common states
            setBillFile(null);
            setNote('');
            setTransactionDate(new Date().toISOString().slice(0, 16));
            setIsSubmitting(false);
            setSelectedCompanyAcc(null);
            setSenderText('');
            setReceiverText('');
            setIsSimpleMode(false);
            
            setManualTransactions([{ 
                id: Date.now(), 
                sender: null, 
                receiver: null, 
                amount: '', 
                note: '', 
                file: null 
            }]);

            if (readOnly && savedTransactions.length > 0) {
                setManualTransactions(savedTransactions);
            } else {
                 setManualTransactions([{ 
                    id: Date.now(), sender: null, receiver: null, amount: '', note: '', file: null 
                }]);
            }

            // Logic chọn Mode mặc định cho các loại thanh toán thường
            let defaultMode = 'OUT';
            let defaultPartner = null;
            
            const firstItem = items.length > 0 ? items[0] : null;
            const isRefundItem = type === 'REFUND' || (type === 'PAYABLE' && firstItem && firstItem.payable_type === 'REFUND');

            if (type === 'BOOKING') {
                defaultMode = 'IN';
                defaultPartner = PARTNER_OPTIONS.find(o => o.id === 'CUSTOMER');
            } else if (isRefundItem) { 
                defaultMode = 'OUT'; 
                defaultPartner = PARTNER_OPTIONS.find(o => o.id === 'CUSTOMER'); 
            } else {
                defaultMode = 'OUT';
                defaultPartner = PARTNER_OPTIONS.find(o => o.id === 'SUPPLIER');
            }

            // Load Data Shareholders nếu là mode Phân bổ
            if (isUnifiedMode) {
                API.client.get('/shareholder-profiles/')
                    .then(res => {
                        const rawData = res.data;
                        const data = Array.isArray(rawData) ? rawData : (rawData.results || []);
                        setShareholders(data);
                    })
                    .catch(err => {
                        console.error("Lỗi load Shareholders:", err);
                        setShareholders([]);
                    });
            } else {
                setBillMode(defaultMode);
                setSelectedPartnerType(defaultPartner);

                if (firstItem) {
                    if (isRefundItem) {
                        setReceiverText(firstItem.receiver_info || firstItem.bank_info || '');
                        setSenderText('');
                    } else {
                        const autoText = getAutoText(firstItem, defaultPartner?.id);
                        if (defaultMode === 'IN') setSenderText(autoText);
                        else setReceiverText(autoText);
                    }
                }
            }

            // Tính tổng tiền mặc định
            let total = 0;
            if (type === 'PAYABLE' || type === 'COLLECTION') {
                total = items.reduce((sum, item) => sum + parseFloat(item.amount || item.remain_amount || 0), 0);
            } else if (type === 'CASHFLOW_DISTRIBUTION') { // <--- THÊM LOGIC NÀY
                total = distributionSummary ? distributionSummary.totalProfit : 0;
            } else {
                total = items.reduce((sum, item) => sum + parseFloat(item.amount || item.total_price || 0), 0);
            }
            
            setRealAmount(total); 
            setAmountStr(formatCurrency(Math.abs(total)));

            Promise.all([
                API.fetchAllFinancialAccounts(),
                (isUnifiedMode && !snapshotData) ? API.client.get('/shareholder-profiles/') : Promise.resolve({ data: [] })
            ]).then(([accRes, shRes]) => {
                const allAccounts = accRes.data || [];
                const companyAccs = allAccounts.filter(a => a.account_type === 'COMPANY');
                setCompanyAccounts(allAccounts);

                if (isUnifiedMode) {
                    let shData = [];
                    
                    if (snapshotData && snapshotData.length > 0) {
                        shData = snapshotData;
                    } else {
                        shData = Array.isArray(shRes.data) ? shRes.data : (shRes.data.results || []);
                    }
                    
                    setShareholders(shData);

                    const merged = [
                        ...companyAccs.map(a => ({ 
                            ...a, 
                            uniqueId: `COMP_${a.id}`, 
                            label: a.name, 
                            type: 'COMPANY',
                            realAccountId: a.id
                        })),
                        
                        ...shData.map(s => {
                            const userId = s.user || s.id; 
                            
                            const personalAcc = allAccounts.find(acc => 
                                acc.account_type === 'PERSONAL' && acc.owner === userId
                            );

                            return { 
                                ...s, 
                                uniqueId: `USER_${userId}`, 
                                label: s.full_name || s.username, 
                                type: 'SHAREHOLDER',
                                id: userId,
                                realAccountId: personalAcc ? personalAcc.id : null,
                                bank_info: personalAcc ? `${personalAcc.bank_name} - ${personalAcc.account_number}` : ''
                            };
                        })
                    ];
                    setCombinedAccounts(merged);
                }
            }).catch(err => console.error("Lỗi load data:", err));
        }
    }, [open, items, type, isUnifiedMode, snapshotData]);

    useEffect(() => {
        if (!isUnifiedMode || !shareholders.length || !distributionSummary) {
            setCalculatedTransactions([]);
            return;
        }

        const { totalProfit, totalCost, payers = {}, companyPaidAmount = 0 } = distributionSummary;

        if (type === 'EXPENSE_DISTRIBUTION') {

            const transactions = shareholders.map(sh => {
                const debtPct = sh.debt_percentage || 0;
                const userId = sh.user || sh.id;

                // Nghĩa vụ phải chịu = Tổng chi phí * % Nợ
                const obligationAmount = totalCost * (debtPct / 100);

                // Số tiền đã ứng chi
                const payerData = payers[userId] || { paid: 0 };
                const amountPaid = payerData.paid || 0;

                // Số tiền cần thanh toán thêm (hoặc nhận lại nếu âm)
                const netAmount = obligationAmount - amountPaid;

                return {
                    id: userId,
                    type: 'SHAREHOLDER',
                    fullName: sh.full_name || sh.username,
                    profitPct: 0,
                    debtPct: debtPct,
                    obligationAmount: obligationAmount,
                    amountPaid: amountPaid,
                    netAmount: netAmount
                };
            });

            // TÍNH TOÁN CHO CÔNG TY (Nếu cổ đông không gánh 100% chi phí)
            const totalShareholderDebtPct = shareholders.reduce((sum, s) => sum + (s.debt_percentage || 0), 0);
            const companyDebtPct = Math.max(0, 100 - totalShareholderDebtPct);
            const companyObligation = totalCost * (companyDebtPct / 100);
            const companyNet = companyObligation - companyPaidAmount;

            const companyRow = {
                id: 'COMPANY',
                type: 'COMPANY',
                fullName: 'QUỸ CÔNG TY',
                profitPct: 0,
                debtPct: companyDebtPct,
                obligationAmount: companyObligation,
                amountPaid: companyPaidAmount,
                netAmount: companyNet
            };

            setCalculatedTransactions([companyRow, ...transactions]);
        } else {
            let totalDistributedProfit = 0;
            const { companyHoldings = { paid: 0, received: 0 } } = distributionSummary;

            // 1. Tính toán cho Cổ đông
            const transactions = shareholders.map(sh => {
                const profitPct = sh.profit_percentage || 0;
                const debtPct = sh.debt_percentage || 0;
                const userId = sh.user || sh.id;

                const profitShare = totalProfit * (profitPct / 100);
                totalDistributedProfit += profitShare;

                const payerData = payers[userId] || { paid: 0, received: 0 };
                const amountPaid = payerData.paid || 0;
                const amountReceived = payerData.received || 0;

                // Holding = Đã thu vào túi - Đã tự bỏ tiền túi ra chi
                const holding = amountReceived - amountPaid;
                
                // netAmount = Cổ phần đáng được nhận - Số tiền đang cầm
                const netAmount = profitShare - holding;

                return {
                    id: userId,
                    type: 'SHAREHOLDER',
                    fullName: sh.full_name || sh.username,
                    profitPct,
                    debtPct,
                    profitShare,
                    amountPaid,
                    amountReceived,
                    holding,
                    netAmount
                };
            });

            // 2. Tính toán đối soát cho Quỹ Công Ty
            const companyProfitShare = totalProfit - totalDistributedProfit; 
            const companyHolding = companyHoldings.received - companyHoldings.paid;
            const companyNetAmount = companyProfitShare - companyHolding;

            const companyData = {
                id: 'COMPANY',
                type: 'COMPANY',
                fullName: 'QUỸ CÔNG TY',
                profitPct: 'N/A',
                debtPct: 'N/A',
                profitShare: companyProfitShare,
                amountPaid: companyHoldings.paid,
                amountReceived: companyHoldings.received,
                holding: companyHolding,
                netAmount: companyNetAmount
            };

            // Đưa Quỹ Công ty lên đầu danh sách để dễ nhìn
            setCalculatedTransactions([companyData, ...transactions]);
        }
    }, [isUnifiedMode, shareholders, distributionSummary]);

    // THUẬT TOÁN TÍNH TOÁN GỢI Ý CHUYỂN TIỀN (SETTLEMENT ALGORITHM)
    useEffect(() => {
        if (!calculatedTransactions || calculatedTransactions.length === 0) {
            setTransferSuggestions([]);
            return;
        }

        let debtors = [];   // Cầm THỪA tiền -> Phải TRẢ RA
        let creditors = []; // Bị THIẾU tiền -> Phải THU VỀ

        calculatedTransactions.forEach(t => {
            if (type === 'EXPENSE_DISTRIBUTION') {
                if (t.netAmount > 1) debtors.push({ ...t, oweAmount: t.netAmount });
                else if (t.netAmount < -1) creditors.push({ ...t, receiveAmount: -t.netAmount });
            } else {
                // Logic Lợi nhuận:
                // netAmount < 0: Đang cầm nhiều hơn phần lãi được chia -> Phải ói tiền ra (Debtor)
                // netAmount > 0: Chưa lấy đủ phần lãi -> Cần được nhận thêm tiền (Creditor)
                if (t.netAmount < -1) {
                    debtors.push({ ...t, oweAmount: Math.abs(t.netAmount) });
                } else if (t.netAmount > 1) {
                    creditors.push({ ...t, receiveAmount: t.netAmount });
                }
            }
        });

        // Ưu tiên thanh toán các khoản lớn trước
        debtors.sort((a, b) => b.oweAmount - a.oweAmount);
        creditors.sort((a, b) => b.receiveAmount - a.receiveAmount);

        let suggestions = [];
        let i = 0, j = 0;

        while (i < debtors.length && j < creditors.length) {
            let debtor = debtors[i];
            let creditor = creditors[j];

            let transferAmount = Math.min(debtor.oweAmount, creditor.receiveAmount);

            if (transferAmount > 1) {
                suggestions.push({
                    from: debtor,
                    to: creditor,
                    amount: transferAmount
                });
            }

            debtor.oweAmount -= transferAmount;
            creditor.receiveAmount -= transferAmount;

            if (debtor.oweAmount < 1) i++;
            if (creditor.receiveAmount < 1) j++;
        }

        setTransferSuggestions(suggestions);
    }, [calculatedTransactions, type]);

    const applySuggestionsToForm = () => {
        if (transferSuggestions.length === 0) return;
        
        const newTransactions = transferSuggestions.map((sug, index) => {
            // Tìm tài khoản gửi
            let senderAcc = null;
            if (sug.from.type === 'COMPANY') {
                senderAcc = combinedAccounts.find(a => a.type === 'COMPANY'); // Chọn TK Công ty mặc định
            } else {
                senderAcc = combinedAccounts.find(a => a.uniqueId === `USER_${sug.from.id}`);
            }
            
            // Tìm tài khoản nhận
            let receiverAcc = null;
            if (sug.to.type === 'COMPANY') {
                receiverAcc = combinedAccounts.find(a => a.type === 'COMPANY');
            } else {
                receiverAcc = combinedAccounts.find(a => a.uniqueId === `USER_${sug.to.id}`);
            }
            
            return {
                id: Date.now() + index,
                sender: senderAcc || null,
                receiver: receiverAcc || null,
                amount: Math.round(sug.amount).toString(),
                note: `Cấn trừ: ${sug.from.fullName} trả cho ${sug.to.fullName}`,
                file: null
            };
        });

        setManualTransactions(newTransactions);
        setIsSimpleMode(false); 
        if (showToast) showToast("Đã tự động điền gợi ý vào form!", "success");
    };

    // --- Helper Functions cho Mode Thường ---
    const handlePartnerTypeChange = (val) => {
        setSelectedPartnerType(val);
        if (val && items.length > 0) {
            const autoText = getAutoText(items[0], val.id);
            if (billMode === 'IN') setSenderText(autoText);
            else setReceiverText(autoText);
        }
    };

    const handleCompanyAccChange = (val) => setSelectedCompanyAcc(val);
    
    const handleModeChange = (e) => {
        const newMode = e.target.value;
        setBillMode(newMode);
        if (items.length > 0 && selectedPartnerType) {
             const text = getAutoText(items[0], selectedPartnerType.id);
             if (newMode === 'IN') {
                 setSenderText(text);
                 setReceiverText('');
             } else {
                 setReceiverText(text);
                 setSenderText('');
             }
        } else {
             setSenderText('');
             setReceiverText('');
        }
        setSelectedCompanyAcc(null);
    };

    const addTransactionRow = () => {
        setManualTransactions(prev => [
            ...prev, 
            { id: Date.now(), sender: null, receiver: null, amount: '', note: '', file: null }
        ]);
    };

    const removeTransactionRow = (rowId) => {
        if (manualTransactions.length > 1) {
            setManualTransactions(prev => prev.filter(r => r.id !== rowId));
        }
    };

    const updateTransactionRow = (rowId, field, value) => {
        setManualTransactions(prev => prev.map(row => {
            if (row.id === rowId) {
                return { ...row, [field]: value };
            }
            return row;
        }));
    };

    const handleOpenBillDetail = (row) => {
        if (!row.fileUrl) return;

        let billTypeTitle = 'PHÂN BỔ LỢI NHUẬN';
        if (type === 'EXPENSE_DISTRIBUTION') billTypeTitle = 'PHÂN BỔ CHI PHÍ';
        if (type === 'CASHFLOW_DISTRIBUTION') billTypeTitle = 'PHÂN BỔ DÒNG TIỀN';

        const billData = {
            type: billTypeTitle, // Dùng biến vừa khai báo
            amount: row.amount,
            note: row.note,
            imageUrl: row.fileUrl,
            supplierName: row.receiverLabel || row.receiver?.label || '---',
            status: 'PAID',
            transaction_date: row.transactionDate || new Date().toISOString(),
            sender_info: row.senderLabel || row.sender?.label,
            receive_account_name: row.receiverLabel || row.receiver?.label,
            payment_account_name: row.senderLabel || (row.sender ? row.sender.label : null), 
        };
        setViewBillDetail(billData);
    };

    // --- RENDER UI: UNIFIED MODE ---
    const renderUnifiedUI = () => {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* 1. BẢNG TỔNG QUAN */}
                {distributionSummary && (
                    <Paper sx={{ py: 0.5, border: '1px solid #ddd', bgcolor: '#fafafa' }} elevation={0}>
                        <Grid container spacing={2}>
                            {/* Cột 1: Số lượng */}
                            <Grid item flex={1} sx={{ textAlign: 'center', borderRight: '1px solid #eee' }}>
                                <Typography variant="caption" color="text.secondary">
                                    {type === 'EXPENSE_DISTRIBUTION' ? 'SỐ KHOẢN CHI' : 'SỐ BOOKING'}
                                </Typography>
                                <Typography variant="body1" fontWeight="bold">{items.length}</Typography>
                            </Grid>

                            {/* Cột 2: Tổng Doanh thu (Ẩn nếu là Chi phí chung) */}
                            {type !== 'EXPENSE_DISTRIBUTION' && (
                                <Grid item flex={1} sx={{ textAlign: 'center', borderRight: '1px solid #eee' }}>
                                    <Typography variant="caption" color="text.secondary">TỔNG DOANH THU</Typography>
                                    <Typography variant="body1" color="success.main" fontWeight="bold">
                                        {formatCurrency(distributionSummary.totalRevenue)}
                                    </Typography>
                                </Grid>
                            )}

                            {/* Cột 3: Tổng Chi phí (Luôn hiện) */}
                            <Grid item flex={1} sx={{ textAlign: 'center', borderRight: '1px solid #eee' }}>
                                <Typography variant="caption" color="text.secondary">TỔNG CHI PHÍ</Typography>
                                <Typography variant="body1" color="error.main" fontWeight="bold">
                                    {formatCurrency(distributionSummary.totalCost)}
                                </Typography>
                            </Grid>

                            {/* Cột 4: Lợi nhuận ròng (Ẩn nếu là Chi phí chung) */}
                            {type !== 'EXPENSE_DISTRIBUTION' && (
                                <Grid item flex={1} sx={{ textAlign: 'center' }}>
                                    <Typography variant="caption" color="text.secondary">LỢI NHUẬN RÒNG</Typography>
                                    <Typography variant="body1" color="#007373" fontWeight="bold">
                                        {formatCurrency(distributionSummary.totalProfit)}
                                    </Typography>
                                </Grid>
                            )}
                        </Grid>
                    </Paper>
                )}

                {/* 2. BẢNG CHI TIẾT TÍNH TOÁN */}
                <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
                    <Table size="small">
                        <TableHead sx={{ bgcolor: '#eee' }}>
                            <TableRow>
                                <TableCell>Cổ đông</TableCell>
                                <TableCell align="center">Tỷ lệ {type === 'EXPENSE_DISTRIBUTION' ? 'Nợ' : ''}</TableCell>
                                
                                {/* HEADER ĐỘNG */}
                                {type === 'EXPENSE_DISTRIBUTION' ? (
                                    <>
                                        <TableCell align="right">Phải chịu</TableCell>
                                        <TableCell align="right">Đã ứng chi</TableCell>
                                    </>
                                ) : (
                                    <>
                                        <TableCell align="right">Chia LN</TableCell>
                                        <TableCell align="right">Đã chi</TableCell>
                                        <TableCell align="right">Đã thu</TableCell>
                                    </>
                                )}
                                
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Tổng kết</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {calculatedTransactions.map((row) => (
                                <TableRow key={row.id}>
                                    <TableCell sx={{ fontWeight: 'bold' }}>{row.fullName}</TableCell>
                                    <TableCell align="center">
                                        {type === 'EXPENSE_DISTRIBUTION' ? (
                                            <Typography variant="body2" fontWeight="bold" color="error.main">{row.debtPct}%</Typography>
                                        ) : (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.5 }}>
                                                <Typography variant="body2" fontWeight="bold" color="success.main">{row.profitPct}</Typography>
                                                <Typography variant="body2" color="text.secondary">-</Typography>
                                                <Typography variant="body2" fontWeight="bold" color="error.main">{row.debtPct}</Typography>
                                            </Box>
                                        )}
                                    </TableCell>
                                    
                                    {/* BODY ĐỘNG */}
                                    {type === 'EXPENSE_DISTRIBUTION' ? (
                                        <>
                                            <TableCell align="right">{formatCurrency(row.obligationAmount)}</TableCell>
                                            <TableCell align="right" sx={{ color: 'primary.main' }}>{formatCurrency(row.amountPaid)}</TableCell>
                                        </>
                                    ) : (
                                        <>
                                            <TableCell align="right" sx={{ color: row.profitShare >= 0 ? 'success.main' : 'error.main', fontWeight: 'bold' }}>
                                                {row.profitShare >= 0 ? '+' : '-'}{formatCurrency(Math.abs(row.profitShare))}
                                            </TableCell>
                                            <TableCell align="right" sx={{ color: 'primary.main' }}>
                                                {row.amountPaid > 0 ? `+${formatCurrency(row.amountPaid)}` : '-'}
                                            </TableCell>
                                            <TableCell align="right" sx={{ color: '#ff9800' }}>
                                                {row.amountReceived > 0 ? `-${formatCurrency(row.amountReceived)}` : '-'}
                                            </TableCell>
                                        </>
                                    )}

                                    <TableCell align="right">
                                        {(() => {
                                            // Xác định số tiền Nợ
                                            let oweAmount = type === 'EXPENSE_DISTRIBUTION' ? row.netAmount : -row.netAmount;
                                            let isOwe = oweAmount > 0;
                                            let isZero = Math.abs(oweAmount) < 1;

                                            if (isZero) return <Typography variant="body2" fontWeight="bold" color="text.secondary">Hòa toán (0)</Typography>;

                                            return (
                                                <Typography 
                                                    variant="body2" 
                                                    fontWeight="bold" 
                                                    sx={{ 
                                                        color: isOwe ? '#d32f2f' : '#2e7d32', // Nợ (phải trả) là Đỏ, Được nhận là Xanh
                                                        bgcolor: isOwe ? '#ffebee' : '#e8f5e9',
                                                        py: 0.5, px: 1, borderRadius: 1, display: 'inline-block'
                                                    }}
                                                >
                                                    {isOwe ? 'CẦN TRẢ: ' : 'CẦN THU: '}
                                                    {formatCurrency(Math.abs(oweAmount))}
                                                </Typography>
                                            );
                                        })()}
                                    </TableCell>
                                </TableRow>
                            ))}
                            
                            {/* Thông báo nếu Công ty trả */}
                            {type === 'EXPENSE_DISTRIBUTION' && distributionSummary?.isPaidByCompany && (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ color: 'text.secondary', fontStyle: 'italic', py: 2 }}>
                                        Khoản chi này được thanh toán bởi Công ty. Không cần phân bổ giữa các Cổ đông.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* 2.5. GỢI Ý THANH TOÁN NỘI BỘ (MỚI - GỌN GÀNG 1 DÒNG) */}
                {transferSuggestions.length > 0 && (
                    <Paper sx={{ p: 1, mt: 2, bgcolor: '#fff8e1', border: '1px solid #ffecb3' }} elevation={0}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#f57c00', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <AutoFixHighIcon fontSize="small" />
                                GỢI Ý THANH TOÁN NỘI BỘ
                            </Typography>
                            {!readOnly && (
                                <Button
                                    size="small" variant="contained" color="warning"
                                    sx={{ textTransform: 'none', boxShadow: 'none', fontWeight: 'bold', fontSize: '0.8rem' }}
                                    onClick={applySuggestionsToForm}
                                >
                                    SỬ DỤNG GỢI Ý
                                </Button>
                            )}
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2 }}>
                            {transferSuggestions.map((sug, idx) => (
                                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.3, px: 1, '&:not(:last-child)': { borderBottom: '1px solid #ffecb3' } }}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>Người chuyển:</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#d32f2f', ml: -0.5 }}>{sug.from.fullName}</Typography>
                                    <ArrowRightAltIcon sx={{ color: 'text.secondary', fontSize: '1.1rem', ml: 0.5, mr: 0.5 }} />
                                    
                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>Số tiền:</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#2e7d32', ml: -0.5 }}>{formatCurrency(sug.amount)} ₫</Typography>
                                    <ArrowRightAltIcon sx={{ color: 'text.secondary', fontSize: '1.1rem', ml: 0.5, mr: 0.5 }} />

                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>Người nhận:</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#2e7d32', ml: -0.5 }}>{sug.to.fullName}</Typography>
                                </Box>
                            ))}
                        </Box>
                    </Paper>
                )}

                {!readOnly && (
                    <Box sx={{ bgcolor: '#e3f2fd', p: 1, borderRadius: 1, border: '1px solid #90caf9' }}>
                        <FormControlLabel
                            control={
                                <Checkbox 
                                    checked={isSimpleMode} 
                                    onChange={(e) => setIsSimpleMode(e.target.checked)} 
                                    color="primary"
                                />
                            }
                            label={
                                <Typography variant="body2" fontWeight="bold" color="#1565c0">
                                    Chỉ đánh dấu "Đã phân bổ" (Không tạo giao dịch tiền tệ)
                                </Typography>
                            }
                        />
                    </Box>
                )}
            </Box>
        );
    };

// --- HANDLE SUBMIT ---
    const handleSubmit = async () => {
        setIsSubmitting(true);
        try {
            const formData = new FormData();
            
            // --- CASE 1: THANH TOÁN CHI PHÍ (EXTERNAL) ---
            if (type === 'EXPENSE_PAYMENT') {
                formData.append('ids', items.map(i => i.id).join(','));
                
                if (selectedCompanyAcc) formData.append('payment_account_id', selectedCompanyAcc.id);
                if (billFile) formData.append('bill_image', billFile);
                
                formData.append('receiver_info', receiverText); 
                formData.append('payment_note', note); 
                formData.append('transaction_date', transactionDate);
                
                await API.bulkPayExpenses(formData);
                if (showToast) showToast("Thanh toán chi phí thành công!", "success");
                if (onSuccess) onSuccess();
                onClose(); // Tự động đóng popup khi thành công
            }
            // --- CASE 2: PHÂN BỔ CHI PHÍ (INTERNAL) ---
            else if (type === 'EXPENSE_DISTRIBUTION') {
                formData.append('expense_ids', items.map(i => i.id).join(','));

                // NẾU SIMPLE MODE -> Gửi mảng rỗng
                let cleanTransactions = [];
                if (!isSimpleMode) {
                    cleanTransactions = manualTransactions.map(t => ({
                        sender: t.sender,
                        receiver: t.receiver, 
                        amount: t.amount,
                        note: t.note
                    }));
                }

                formData.append('transactions_json', JSON.stringify(cleanTransactions));

                if (!isSimpleMode) {
                    manualTransactions.forEach((t, index) => {
                        if (t.file) {
                            formData.append(`bill_${index}`, t.file);
                        }
                    });
                }

                await API.bulkDistributeExpenses(formData);
                if (showToast) showToast(isSimpleMode ? "Đã cập nhật trạng thái phân bổ!" : "Phân bổ chi phí thành công!", "success");
                if (onSuccess) onSuccess();
                onClose(); // Tự động đóng popup khi thành công
            }
            // --- CASE 3: THANH TOÁN THƯỜNG (BOOKING, PAYABLE...) ---
            else if (!isUnifiedMode) {
                formData.append('note', note);
                if (billFile) formData.append('bill_image', billFile);
                if (selectedCompanyAcc) formData.append('payment_account_id', selectedCompanyAcc.id);
                formData.append('sender_info', senderText);
                formData.append('receiver_info', receiverText);
                formData.append('transaction_date', transactionDate);

                const transactionAmount = Math.abs(realAmount);

                if (type === 'REFUND') {
                    const itemsPayload = items.map(item => ({
                        id: item.id || item.booking_code,
                        amount: realAmount > 0 ? (realAmount / items.length) : item.amount,
                        bank_info: item.sender_info || item.receiver_info 
                    }));
                    formData.append('items', JSON.stringify(itemsPayload));
                    await API.bulkCreateRefunds(formData);
                    if (showToast) showToast("Đã hoàn tiền thành công!", "success");
                    if (onSuccess) onSuccess();
                    onClose(); // Tự động đóng popup khi thành công
                } else {
                    const payloadItems = items.map(item => {
                        let pType = item.payable_type;
                        if (type === 'BOOKING') pType = 'BOOKING_PAYMENT';
                        else if (type === 'COLLECTION') pType = 'COLLECTION';

                        return {
                            id: item.id,
                            payable_type: pType,
                            amount: (items.length === 1) ? transactionAmount : item.amount 
                        };
                    });
                    formData.append('items', JSON.stringify(payloadItems));
                    await API.bulkPayPayables(formData);
                    if (showToast) showToast('Thao tác thành công!', 'success');
                    if (onSuccess) onSuccess();
                    onClose(); // Tự động đóng popup khi thành công
                }
            } 
            // --- PHÂN BỔ DÒNG TIỀN ---
            else if (type === 'CASHFLOW_DISTRIBUTION') {
                // Trích xuất id và type (PAYMENT/PAYABLE) từ items
                const payloadItems = items.map(i => ({ id: i.id, type: i.type }));
                formData.append('items', JSON.stringify(payloadItems));

                let cleanTransactions = [];
                if (!isSimpleMode) {
                    cleanTransactions = manualTransactions.map(t => ({
                        sender: t.sender, receiver: t.receiver, amount: t.amount, note: t.note
                    }));
                }
                formData.append('transactions_json', JSON.stringify(cleanTransactions));

                if (!isSimpleMode) {
                    manualTransactions.forEach((t, index) => {
                        if (t.file) formData.append(`bill_${index}`, t.file);
                    });
                }

                await API.bulkDistributeTransactions(formData);
                if (showToast) showToast(isSimpleMode ? "Đã cập nhật trạng thái!" : "Phân bổ dòng tiền thành công!", "success");
                if (onSuccess) onSuccess();
                onClose(); // Tự động đóng popup khi thành công
            }
        } catch (error) {
            console.error(error);
            if (showToast) showToast(error.response?.data?.message || 'Có lỗi xảy ra', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth={isUnifiedMode ? "md" : "sm"} fullWidth PaperProps={{ sx: { maxHeight: '90vh' } }}>
            <DialogTitle sx={{ bgcolor: isUnifiedMode ? '#007373' : (billMode === 'IN' ? '#2e7d32' : '#d32f2f'), color: 'white', py: 1.5 }}>
                <Typography variant="h6" fontWeight="bold">{getTitle()}</Typography>
            </DialogTitle>
            
            <DialogContent dividers sx={{ px: 2, py: 2 }}>
                {isUnifiedMode ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {!readOnly && renderUnifiedUI()} 

                        {/* DANH SÁCH GIAO DỊCH */}
                        {!isSimpleMode && (
                            <>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'end' }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#007373' }}>
                                        {readOnly ? 'LỊCH SỬ GIAO DỊCH PHÂN BỔ' : 'CÁC GIAO DỊCH THỰC HIỆN'}
                                    </Typography>
                                    {!readOnly && (
                                        <Button 
                                            startIcon={<AddIcon />} 
                                            size="small" 
                                            variant="outlined" 
                                            onClick={addTransactionRow}
                                            sx={{ textTransform: 'none' }}
                                        >
                                            Thêm giao dịch
                                        </Button>
                                    )}
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {manualTransactions.map((row, index) => (
                                        <Paper key={row.id} sx={{ p: 2, border: '1px dashed #ccc', bgcolor: '#fff', position: 'relative' }}>
                                            {!readOnly && manualTransactions.length > 1 && (
                                                <IconButton 
                                                    size="small" color="error" 
                                                    onClick={() => removeTransactionRow(row.id)}
                                                    sx={{ position: 'absolute', top: 5, right: 5 }}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                            <Box>
                                                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: 2 }}>
                                                    <Grid item flex={1}>
                                                        <CustomLabel title="TK Gửi" required />
                                                        {readOnly ? (
                                                            <Typography variant="body1" fontWeight="bold">{row.senderLabel || '---'}</Typography>
                                                        ) : (
                                                            <Autocomplete
                                                                options={combinedAccounts}
                                                                getOptionLabel={(opt) => opt.label}
                                                                value={row.sender}
                                                                onChange={(e, val) => updateTransactionRow(row.id, 'sender', val)}
                                                                renderInput={(params) => <TextField {...params} placeholder="Chọn TK..." size="small" />}
                                                                isOptionEqualToValue={(option, value) => option.uniqueId === value.uniqueId}
                                                            />
                                                        )}
                                                    </Grid>
                                                    <Grid item flex={1}>
                                                        <CustomLabel title="TK Nhận" required />
                                                        {readOnly ? (
                                                            <Box>
                                                                <Typography variant="body1" fontWeight="bold">{row.receiverLabel || row.receiver?.label}</Typography>
                                                                {row.receiverInfo && <Typography variant="caption" color="text.secondary">{row.receiverInfo}</Typography>}
                                                            </Box>
                                                        ) : (
                                                            <Autocomplete
                                                                options={combinedAccounts}
                                                                getOptionLabel={(opt) => opt.label}
                                                                value={row.receiver}
                                                                onChange={(e, val) => updateTransactionRow(row.id, 'receiver', val)}
                                                                renderInput={(params) => <TextField {...params} placeholder="Chọn TK..." size="small" />}
                                                                isOptionEqualToValue={(option, value) => option.uniqueId === value.uniqueId}
                                                            />
                                                        )}
                                                    </Grid>
                                                    <Grid item flex={1}>
                                                        <CustomLabel title="Số tiền" required />
                                                        {readOnly ? (
                                                            <Typography variant="body1" fontWeight="bold" color="#d32f2f">
                                                                {formatCurrency(row.amount)} VND
                                                            </Typography>
                                                        ) : (
                                                            <TextField 
                                                                fullWidth size="small" placeholder="0"
                                                                value={row.amount ? formatCurrency(row.amount) : ''}
                                                                onChange={(e) => {
                                                                    const val = e.target.value.replace(/[^0-9]/g, '');
                                                                    updateTransactionRow(row.id, 'amount', val);
                                                                }}
                                                                InputProps={{
                                                                    endAdornment: <InputAdornment position="end" sx={{fontSize:'0.7rem'}}>VND</InputAdornment>
                                                                }}
                                                            />
                                                        )}
                                                    </Grid>
                                                </Box>
                                                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: 2, mt: 2 }}>
                                                    <Grid item flex={1}>
                                                        {readOnly ? (
                                                            <Box>
                                                                <CustomLabel title="Ghi chú" />
                                                                <Typography variant="body2">{row.note || '---'}</Typography>
                                                            </Box>
                                                        ) : (
                                                            <TextField 
                                                                fullWidth size="small" placeholder="Nhập ghi chú..."
                                                                value={row.note}
                                                                onChange={(e) => updateTransactionRow(row.id, 'note', e.target.value)}
                                                            />
                                                        )}
                                                    </Grid>
                                                    <Grid item flex={1}>
                                                        {readOnly ? (
                                                            row.fileUrl ? (
                                                                <Button
                                                                    variant="outlined" size="small"
                                                                    startIcon={<VisibilityIcon />} // Nhớ import icon
                                                                    onClick={() => handleOpenBillDetail(row)}
                                                                    sx={{ textTransform: 'none', width: '100%' }}
                                                                >
                                                                    Xem Bill
                                                                </Button>
                                                            ) : <Typography variant="caption" color="text.secondary">Không có bill</Typography>
                                                        ) : (
                                                            <Button
                                                                component="label" variant="outlined" size="small" 
                                                                startIcon={<CloudUploadIcon />}
                                                                sx={{ 
                                                                    textTransform: 'none', 
                                                                    borderStyle: 'dashed', 
                                                                    color: row.file ? 'green' : 'text.secondary',
                                                                    borderColor: row.file ? 'green' : undefined,
                                                                    width: '100%', height: '40px', px: 1
                                                                }}
                                                            >
                                                                {row.file ? (
                                                                    <Typography variant="caption" noWrap sx={{ maxWidth: '100%', fontWeight: 'bold' }}>
                                                                        {row.file.name}
                                                                    </Typography>
                                                                ) : "Đăng Bill"}
                                                                <input 
                                                                    type="file" hidden accept="image/*" 
                                                                    onChange={(e) => updateTransactionRow(row.id, 'file', e.target.files[0])} 
                                                                />
                                                            </Button>
                                                        )}
                                                    </Grid>
                                                </Box>
                                            </Box>
                                        </Paper>
                                    ))}
                                </Box>
                            </>
                        )}
                    </Box>
                ) : (
                    // 2. RENDER GIAO DIỆN THANH TOÁN THƯỜNG (Giữ nguyên)
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {/* Loại Bill & Số tiền */}
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', bgcolor: '#f5f5f5', p: 1.5, borderRadius: 2 }}>
                            <Box sx={{ flex: 1 }}>
                                 <CustomLabel title="Loại giao dịch" />
                                 <Box sx={{ display: 'flex', height: '42.3px', borderRadius: 1, border: '1px solid #ccc', overflow: 'hidden', cursor: 'pointer' }}>
                                    <Box 
                                        onClick={() => handleModeChange({ target: { value: 'IN' } })}
                                        sx={{ 
                                            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            bgcolor: billMode === 'IN' ? '#e8f5e9' : '#fff',
                                            color: billMode === 'IN' ? '#2e7d32' : '#9e9e9e',
                                            transition: 'all 0.2s', '&:hover': { bgcolor: billMode === 'IN' ? '#c8e6c9' : '#f5f5f5' }
                                        }}
                                    >
                                        BILL THU
                                    </Box>
                                    <Divider orientation="vertical" flexItem sx={{ bgcolor: '#ccc' }} />
                                    <Box 
                                        onClick={() => handleModeChange({ target: { value: 'OUT' } })}
                                        sx={{ 
                                            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            bgcolor: billMode === 'OUT' ? '#ffebee' : '#fff',
                                            color: billMode === 'OUT' ? '#d32f2f' : '#9e9e9e',
                                            transition: 'all 0.2s', '&:hover': { bgcolor: billMode === 'OUT' ? '#ffcdd2' : '#f5f5f5' }
                                        }}
                                    >
                                        BILL CHI
                                    </Box>
                                 </Box>
                            </Box>
                            <Divider orientation="vertical" flexItem />
                            <Box sx={{ flex: 1 }}>
                                <CustomLabel title="Tổng tiền giao dịch" />
                                <TextField
                                    fullWidth size="small"
                                    value={amountStr}
                                    onChange={(e) => {
                                        const val = e.target.value.replace(/[^0-9]/g, '');
                                        setRealAmount(parseInt(val || 0));
                                        setAmountStr(formatCurrency(val));
                                    }}
                                    InputProps={{
                                        endAdornment: <InputAdornment position="end">VND</InputAdornment>,
                                        sx: { fontWeight: 'bold', fontSize: '1.1rem', color: billMode === 'IN' ? '#2e7d32' : '#d32f2f' }
                                    }}
                                />
                            </Box>
                        </Box>

                        <Grid container spacing={2}>
                            {/* Sender Box */}
                            <Grid item sx={{ flex: 1 }}>
                                <Box sx={{ p: 1.5, border: '1px solid #e0e0e0', borderRadius: 2, bgcolor: '#fff' }}>
                                    <CustomLabel title="TÀI KHOẢN CHUYỂN (SENDER)" />
                                    {billMode === 'OUT' ? (
                                        <Autocomplete
                                            options={companyAccounts}
                                            getOptionLabel={(opt) => opt.name}
                                            value={selectedCompanyAcc}
                                            onChange={(e, val) => handleCompanyAccChange(val)}
                                            renderInput={(params) => <TextField {...params} placeholder="Chọn TK Nguồn..." size="small" variant="standard" />}
                                        />
                                    ) : (
                                        <Autocomplete
                                            options={PARTNER_OPTIONS}
                                            getOptionLabel={(opt) => opt.label}
                                            value={selectedPartnerType}
                                            onChange={(e, val) => handlePartnerTypeChange(val)}
                                            renderInput={(params) => <TextField {...params} placeholder="Chọn loại đối tác..." size="small" variant="standard" />}
                                        />
                                    )}
                                    <TextField fullWidth size="small" placeholder="Thông tin chi tiết..." value={senderText} onChange={(e) => setSenderText(e.target.value)} InputProps={{ style: { fontSize: '0.9rem' } }} />
                                </Box>
                            </Grid>

                            {/* Receiver Box */}
                            <Grid item sx={{ flex: 1 }}>
                                <Box sx={{ p: 1.5, border: '1px solid #e0e0e0', borderRadius: 2, bgcolor: '#fff' }}>
                                    <CustomLabel title="TÀI KHOẢN NHẬN (RECEIVER)" />
                                    {billMode === 'IN' ? (
                                        <Autocomplete
                                            options={companyAccounts}
                                            getOptionLabel={(opt) => opt.name}
                                            value={selectedCompanyAcc}
                                            onChange={(e, val) => handleCompanyAccChange(val)}
                                            renderInput={(params) => <TextField {...params} placeholder="Chọn TK Công ty..." size="small" variant="standard" />}
                                        />
                                    ) : (
                                        type === 'EXPENSE_PAYMENT' ? (
                                            <Box sx={{ py: 1 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#555' }}>
                                                    Người nhận tiền
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <Autocomplete
                                                options={PARTNER_OPTIONS}
                                                getOptionLabel={(opt) => opt.label}
                                                value={selectedPartnerType}
                                                onChange={(e, val) => handlePartnerTypeChange(val)}
                                                renderInput={(params) => <TextField {...params} placeholder="Chọn loại đối tác..." size="small" variant="standard" />}
                                            />
                                        )
                                    )}
                                    <TextField fullWidth size="small" placeholder="Thông tin chi tiết..." value={receiverText} onChange={(e) => setReceiverText(e.target.value)} InputProps={{ style: { fontSize: '0.9rem' } }} />
                                </Box>
                            </Grid>
                        </Grid>

                        {/* Upload & Note */}
                        <Box>
                            <Box sx={{ flex: 1 }}>
                                <CustomLabel title="Bill minh chứng" />
                                <Button
                                    component="label" variant="outlined" fullWidth
                                    startIcon={<CloudUploadIcon />}
                                    sx={{ textTransform: 'none', height: 40, borderStyle: 'dashed' }}
                                >
                                    {billFile ? "Đổi ảnh khác" : "Tải ảnh Bill"}
                                    <input type="file" hidden accept="image/*" onChange={(e) => setBillFile(e.target.files[0])} />
                                </Button>
                                {billFile && <Typography variant="caption" sx={{ color: 'primary.main', display: 'block', wordBreak: 'break-all' }}>{billFile.name}</Typography>}
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Box sx={{ flex: 1 }}>
                                <CustomLabel title="Thời gian giao dịch" />
                                <TextField
                                    fullWidth size="small"
                                    type="datetime-local"
                                    value={transactionDate}
                                    onChange={(e) => setTransactionDate(e.target.value)}
                                />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <CustomLabel title="Ghi chú" />
                                <TextField
                                    fullWidth size="small" placeholder="Nội dung chuyển khoản..."
                                    value={note}
                                    onChange={(e) => setNote(e.target.value)}
                                />
                            </Box>
                        </Box>
                    </Box>
                )}
            </DialogContent>
            
            <DialogActions sx={{ p: 2, bgcolor: '#f9f9f9' }}>
                <Button onClick={onClose} disabled={isSubmitting} color="inherit">{readOnly ? "Đóng" : "Hủy bỏ"}</Button>
                {!readOnly && (
                    <Button 
                        onClick={handleSubmit} 
                        variant="contained"
                        disabled={isSubmitting}
                        startIcon={isSubmitting && <CircularProgress size={20} color="inherit"/>}
                        sx={{ 
                            bgcolor: isUnifiedMode ? '#007373' : (billMode === 'IN' ? '#2e7d32' : '#d32f2f'), 
                            '&:hover': { bgcolor: isUnifiedMode ? '#004d40' : (billMode === 'IN' ? '#1b5e20' : '#b71c1c') }, 
                            px: 3 
                        }}
                    >
                        {isSubmitting ? 'Đang xử lý...' : (isUnifiedMode ? 'XÁC NHẬN PHÂN BỔ' : 'XÁC NHẬN')}
                    </Button>
                )}
            </DialogActions>

            <BillDetailDialog 
                open={!!viewBillDetail} 
                onClose={() => setViewBillDetail(null)} 
                billData={viewBillDetail} 
            />
        </Dialog>
    );
};

export default BulkPaymentDialog;